<template>
  <!--
    popup 模式：captcha-element 作为挂载容器，button 隐藏，由外部调用 show() 触发弹窗
    embed 模式：captcha-element 直接内联展示验证码，无需触发按钮
  -->
  <div>
    <div :id="elementId"></div>
    <button
      v-if="mode === 'popup'"
      :id="buttonId"
      style="display: none"
      aria-hidden="true"
    ></button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'

/**
 * 阿里云验证码 2.0 (V3 架构) Web/H5 组件
 *
 * 使用方式：
 * 1. 在需要验证的时机调用组件实例的 show() 方法，弹出验证码弹窗
 * 2. 监听 success 事件，获取 captchaVerifyParam 后原样提交至后端接口
 * 3. 监听 fail 事件处理验证失败
 *
 * 注意：
 * - 验证码 JS 需要在初始化前至少 2s 完成加载（官方要求）
 * - initAliyunCaptcha 不支持重复调用，如参数变化需先销毁再重建
 */

interface Props {
  /** 身份标识，来自阿里云控制台 / appConfig.ali_captcha_conf.prefix */
  prefix: string
  /** 验证场景 ID，来自阿里云控制台 / appConfig.ali_captcha_conf.scene_id */
  sceneId: string
  /**
   * 验证码模式
   * - popup（默认）：弹出式，通过 show() 手动触发弹窗
   * - embed：嵌入式，验证码直接内联渲染在 element 容器中
   */
  mode?: 'popup' | 'embed'
  /** 所属地区：cn=中国内地，sgp=新加坡 */
  region?: 'cn' | 'sgp'
  /** 语言，默认 cn */
  language?: string
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'popup',
  region: 'cn',
  language: 'cn',
})

const emit = defineEmits<{
  /** 验证码加载成功 */
  loaded: []
  /** 验证通过，返回 captchaVerifyParam，原样提交至后端 */
  success: [captchaVerifyParam: string]
  /** 验证失败 */
  fail: [result: unknown]
  /** SDK 初始化错误 */
  error: [errorInfo: { code: string; msg: string }]
  /** 弹窗关闭 */
  close: []
}>()

// 用随机后缀避免同一页面多实例 id 冲突
const uid = Math.random().toString(36).slice(2, 8)
const elementId = `aliyun-captcha-el-${uid}`
const buttonId = `aliyun-captcha-btn-${uid}`

// 验证码实例，可通过 captchaInstance.value?.show() / hide() 调用
const captchaInstance = ref<{ show: () => void; hide: () => void } | null>(null)

let scriptEl: HTMLScriptElement | null = null
let configScriptEl: HTMLScriptElement | null = null
let initialized = false
const loaded = ref(false)

/** 动态加载 AliyunCaptcha.js（官方要求必须动态加载，不可重复引入） */
function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // 已经加载过则直接 resolve
    if (document.getElementById('aliyun-captcha-js')) {
      resolve()
      return
    }
    scriptEl = document.createElement('script')
    scriptEl.id = 'aliyun-captcha-js'
    scriptEl.type = 'text/javascript'
    scriptEl.src = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js'
    scriptEl.onload = () => resolve()
    scriptEl.onerror = () => reject(new Error('AliyunCaptcha.js 加载失败'))
    document.head.appendChild(scriptEl)
  })
}

/** 注入全局 AliyunCaptchaConfig（region + prefix，必须在 JS 加载前设置） */
function injectConfig() {
  // 移除旧的 config script（如有）
  const old = document.getElementById('aliyun-captcha-config')
  if (old) old.remove()

  configScriptEl = document.createElement('script')
  configScriptEl.id = 'aliyun-captcha-config'
  configScriptEl.textContent = `window.AliyunCaptchaConfig = { region: "${props.region}", prefix: "${props.prefix}" };`
  document.head.insertBefore(configScriptEl, document.head.firstChild)
}

async function init() {
  if (initialized) return

  injectConfig()

  try {
    await loadScript()
  } catch (e) {
    emit('error', { code: 'LOAD_FAILED', msg: String(e) })
    return
  }

  const w = window as any
  if (typeof w.initAliyunCaptcha !== 'function') {
    emit('error', { code: 'INIT_MISSING', msg: 'initAliyunCaptcha is not defined' })
    return
  }

  const initOptions: Record<string, unknown> = {
    SceneId: props.sceneId,
    mode: props.mode,
    element: `#${elementId}`,
    language: props.language,
  }
  // popup 模式才需要 button；embed 模式无触发按钮
  if (props.mode === 'popup') {
    initOptions.button = `#${buttonId}`
  }
  var aliLang = {
    cn: { POPUP_TITLE: '请完成下列验证后继续' },
  }
  w.initAliyunCaptcha({
    ...initOptions,
    upLang: aliLang,
    slideStyle: { width: 320, height: 40 },
    success(captchaVerifyParam: string) {
      console.log('验证码验证成功，参数：', captchaVerifyParam)
      emit('success', captchaVerifyParam)
    },
    fail(result: unknown) {
      emit('fail', result)
    },
    getInstance(instance: { show: () => void; hide: () => void }) {
      captchaInstance.value = instance
    },
    onError(errorInfo: { code: string; msg: string }) {
      emit('error', errorInfo)
    },
    onClose() {
      emit('close')
    },
  })
  initialized = true
}

/** 主动弹出验证码弹窗 */
function show() {
  captchaInstance.value?.show()
}

/** 主动关闭验证码弹窗 */
function hide() {
  captchaInstance.value?.hide()
}

defineExpose({ show, hide })

onMounted(() => {
  init()
})

onUnmounted(() => {
  // 销毁实例，避免内存泄漏
  captchaInstance.value = null
  initialized = false
})
// 通过 captchaInstance 监听实例变化，来判断是否加载成功
watch(
  () => captchaInstance.value,
  (newVal) => {
    // console.log('captchaInstance changed', newVal)
    if (newVal && !loaded.value) {
      loaded.value = true
      emit('loaded')
    }
  },
)
</script>
